import { signal } from '@preact/signals';
import { idbGet, idbSet } from '../lib/idb';
import { Vec3, clamp, norm } from '../lib/math';
import { fetchWithin } from '../lib/net';
import type { FrameContext, PickHit, SkyProvider } from '../render/engine';
import { MOVING_MARGIN, SAT_MIN_SIN, eyeLimitAt } from '../render/nakedeye';
import type { LabelCandidate } from '../render/overlay';
import { STORE, Selection, place, selection, settings, simNow, timeRate, timeVersion } from '../state';
import { FRESH_ORBIT_AGE, MAX_EPOCH_AGE, epochOk } from './orbitAge';
import type { Pass, SatMeta, TrainPasses, WorkerIn } from './sat.worker';
export type { TrainPasses } from './sat.worker';

/** While a saved night is relived, its time; otherwise the two weeks either side are counted from now. */
let orbitsFor: number | null = null;
/** The newest orbit epoch in the catalogue on show (today's, or a relived night's saved orbits); null until one loads. */
let newestEpoch: number | null = null;

/**
 * Why satellites (or one satellite) cannot be shown at `d`, or null when they can. Each orbit is good for about
 * two weeks either side of when it was measured: 'far' when `d` is more than that from today (or from the night
 * being relived), 'old' when it is the orbit data that is too old for `d`.
 */
export function satTimeProblem(d: Date, sat?: { epoch: number }): 'far' | 'old' | null {
  const t = d.getTime();
  if (sat ? epochOk(sat, t) : newestEpoch === null || epochOk({ epoch: newestEpoch }, t)) return null;
  return Math.abs(t - (orbitsFor ?? Date.now())) < MAX_EPOCH_AGE ? 'old' : 'far';
}
export const satTimeOk = (d: Date, sat?: { epoch: number }) => !satTimeProblem(d, sat);

export type { Pass, SatMeta };

const CELESTRAK = 'https://celestrak.org/NORAD/elements/gp.php';
const REFRESH_MS = 12 * 3600e3;
const MIN_RETRY_MS = 2.2 * 3600e3; // CelesTrak asks for at most one download per group per 2 h
/** A question to the satellite worker that has had no answer after this long has failed. */
const REQUEST_MS = 60e3;

/**
 * `rev` goes up each time the worker takes on a new catalogue, so anything listed from the last one is out of
 * date (the new one often has the same satellites, with newer orbits).
 */
/** The satellite status's error once the worker itself has failed (rather than the orbits being out of reach). */
export const WORKER_FAILED = 'The satellite worker failed';
export const satStatus = signal<{ count: number; updated: number | null; source: 'snapshot' | 'cache' | 'live' | 'none'; error?: string; rev: number }>({
  count: 0, updated: null, source: 'none', rev: 0,
});

export function parseCSV(text: string): Record<string, string>[] {
  const lines = text.trim().split(/\r?\n/);
  const head = lines.shift()?.split(',') ?? [];
  return lines.map((l) => {
    const parts = l.split(',');
    // OBJECT_NAME may contain commas in rare cases; rejoin the surplus into the name.
    const extra = parts.length - head.length;
    if (extra > 0) parts.splice(0, extra + 1, parts.slice(0, extra + 1).join(','));
    const o: Record<string, string> = {};
    head.forEach((h, i) => (o[h] = parts[i]));
    return o;
  });
}

/** Friendly names for well-known objects. */
// Catalogue names are in capitals. Words that are acronyms stay that way; names become "Terra", "Cosmos".
const ACRONYMS = new Set(
  'NOAA GOES METOP DMSP NROL TDRS SAOCOM ICESAT CALIPSO SMAP TESS NUSTAR GLAST ALOS ERBS UARS TOPEX CRYOSAT INSAT GSAT IRNSS QZSS MUOS AEHF SBIRS TIMED SORCE GOSAT CBERS KOMPSAT RADARSAT SPOT NASA ESA JAXA ISRO CNES DLR CZ HJ YAOGAN SJ TJS FY ZY BD BEIDOU GLONASS GNSS COSPAS SARSAT ORBCOMM SWARM GRACE SAC OCO TRMM GPM SMOS VRSS ASTRO'.split(' '),
);
function tidyName(n: string): string {
  if (/[a-z]/.test(n)) return n; // already mixed case
  return n.replace(/[A-Z]{4,}/g, (w) => (ACRONYMS.has(w) ? w : w[0] + w.slice(1).toLowerCase()));
}

export function displayName(m: { id: number; name: string }): string {
  const special: Record<number, string> = { 25544: 'ISS', 48274: 'Tiangong', 20580: 'Hubble' };
  if (special[m.id]) return special[m.id];
  const n = m.name.replace(/\s+/g, ' ').trim();
  if (/^STARLINK-/i.test(n)) return 'Starlink ' + n.slice(9);
  // "SL-16 R/B" is a spent rocket stage; "DEB" is debris.
  return tidyName(n).replace(/\s+R\/B$/i, ' rocket stage').replace(/\s+DEB$/i, ' debris');
}

/** Modules docked to a space station have their own catalogue entries but share its passes. */
export const isDockedModule = (m: { id: number; name: string }) => /^(ISS|CSS) \(/i.test(m.name) && m.id !== 25544 && m.id !== 48274;

export function longName(m: { id: number; name: string }): string {
  const special: Record<number, string> = {
    25544: 'International Space Station', 48274: 'Tiangong space station', 20580: 'Hubble Space Telescope',
  };
  return special[m.id] ?? displayName(m);
}

interface GroupCache { csv: string; fetched: number; attempted: number }

/** In development, read saved fixtures instead of CelesTrak unless `ahillah:liveData` is set. */
function useFixtures() {
  try {
    return import.meta.env.DEV && !localStorage.getItem(`${STORE}liveData`);
  } catch {
    return import.meta.env.DEV;
  }
}

type GroupRows = { rows: Record<string, string>[]; fetched: number; live: boolean } | null;
/** One download per group at a time, however many callers ask (CelesTrak blocks repeat downloads). */
const inflight = new Map<string, Promise<GroupRows>>();
function loadGroup(group: string): Promise<GroupRows> {
  let p = inflight.get(group);
  if (!p) {
    p = fetchGroup(group).finally(() => inflight.delete(group));
    inflight.set(group, p);
  }
  return p;
}

/** A file's text, or '' if it is missing or does not come. */
const textOf = (url: string, init?: RequestInit) => fetchWithin(url, (r) => (r.ok ? r.text() : ''), init).catch(() => '');

async function fetchGroup(group: string): Promise<GroupRows> {
  if (useFixtures()) {
    if (group !== 'active') return null; // the bundled snapshot covers the small groups
    const text = await textOf('/__fixtures/active.csv');
    return text ? { rows: parseCSV(text), fetched: Date.now(), live: false } : null;
  }
  const key = `gp:${group}`;
  const cached = await idbGet<GroupCache>(key);
  const now = Date.now();
  const fresh = cached && now - cached.fetched < REFRESH_MS;
  const mayTry = !cached || now - cached.attempted > MIN_RETRY_MS;
  if (!fresh && mayTry && navigator.onLine !== false) {
    try {
      let text = await textOf(`${CELESTRAK}?GROUP=${encodeURIComponent(group)}&FORMAT=csv`, { cache: 'no-store' });
      // CelesTrak limits downloads per network; if it refuses, use Ahillah's shared two-hourly copy.
      if (!text.startsWith('OBJECT_NAME')) text = await textOf(`${import.meta.env.BASE_URL}api/gp?group=${encodeURIComponent(group)}`);
      if (text.startsWith('OBJECT_NAME')) {
        await idbSet(key, { csv: text, fetched: now, attempted: now });
        return { rows: parseCSV(text), fetched: now, live: true };
      }
      // Not updated / blocked: remember the attempt so we never hammer the server.
      await idbSet(key, { csv: cached?.csv ?? '', fetched: cached?.fetched ?? 0, attempted: now });
    } catch {
      /* offline */
    }
  }
  if (cached?.csv) return { rows: parseCSV(cached.csv), fetched: cached.fetched, live: false };
  return null;
}

interface Live {
  t0: number;
  t1: number;
  realT: number;
  realDt: number;
  ids: Int32Array;
  data: Float32Array;
  sunAlt: number;
}

interface Shown {
  id: number;
  enu: Vec3;
  mag: number;
  lit: number;
  x: number;
  y: number;
  onScreen: boolean;
}

const STRIDE = 11;
type DistOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export class SatelliteService implements SkyProvider {
  meta = new Map<number, SatMeta>();
  private worker: Worker;
  private live: Live | null = null;
  private shown: Shown[] = [];
  private pts = new Float32Array(0);
  private nPts = 0;
  private trailSeg: number[] = [];
  private trailCol: number[] = [];
  private history = new Map<number, { t: number; v: Vec3; mag: number }[]>();
  private reqId = 0;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void; timer: ReturnType<typeof setTimeout> }>();
  private selTrack: { id: number; times: number[]; pts: number[]; at: number } | null = null;
  /** The catalogue asked for last (so it is not asked for twice). */
  private requestedMode: 'bright' | 'all' | null = null;
  /**
   * Goes up with each catalogue asked for, and as reliving starts or ends: a load that finds it has changed
   * was overtaken, and is dropped.
   */
  private gen = 0;
  private loading: { mode: 'bright' | 'all'; gen: number; done: Promise<void> } | null = null;
  /** The last catalogue sent to the worker: only the answer about that one is taken. */
  private sentRev = 0;
  /** Settles when the worker first holds a catalogue (or the first load found none): until then it would answer "nothing". */
  private firstLoad: Promise<void>;
  private firstLoaded = () => {};
  /** Whether the worker is working out live positions (see syncActive). */
  private active = false;
  private stdmag: Record<string, number> = {};
  /** Showing a saved night's satellites (from their saved orbit data) instead of today's catalogue. */
  private reliving = false;
  /** Called when a bright satellite comes into naked-eye view (for live alerts). */
  onVisible?: (id: number, mag: number, enu: Vec3) => void;
  private visibleSet = new Set<number>();
  /** Why the worker failed, once it has: no question can be answered after that. */
  private broken: string | null = null;
  private alerted = new Map<number, number>();

  constructor() {
    this.firstLoad = new Promise((r) => (this.firstLoaded = r));
    this.worker = new Worker(new URL('./sat.worker.ts', import.meta.url), { type: 'module' });
    this.worker.onmessage = (ev) => this.onMessage(ev.data);
    // A worker that fails leaves its questions unanswered: fail them, so nothing waits for ever.
    this.worker.onerror = () => this.failAll(WORKER_FAILED);
    this.worker.onmessageerror = () => this.failAll(WORKER_FAILED);
    place.subscribe((p) => this.post({ type: 'observer', lat: p.lat, lon: p.lon, height: p.height }));
    timeVersion.subscribe(() => this.syncClock());
    timeRate.subscribe(() => this.syncClock());
    settings.subscribe((s) => {
      const mode = s.satellites === 'all' ? 'all' : 'bright';
      if (s.satellites !== 'off' && mode !== this.requestedMode && !this.reliving) void this.loadData(mode);
      this.syncActive();
    });
    selection.subscribe((sel) => {
      this.post({ type: 'focus', ids: sel?.kind === 'satellite' ? [sel.id] : [] });
      this.selTrack = null;
      this.syncActive();
    });
    document.addEventListener('visibilitychange', () => this.syncActive());
  }

  private post(m: WorkerIn) {
    if (!this.broken) this.worker.postMessage(m);
  }

  private syncClock() {
    const now = Date.now();
    this.post({ type: 'clock', anchorSim: simNow().getTime(), anchorReal: now, rate: timeRate.peek() });
  }

  /**
   * Live positions are worked out only while something shows them: the Satellites layer, a relived night or a
   * chosen satellite (its card), with the app in view. Passes and tracks are still answered when asked for.
   */
  private syncActive() {
    const on = document.visibilityState !== 'hidden' && (settings.peek().satellites !== 'off' || this.reliving || selection.peek()?.kind === 'satellite');
    if (on === this.active) return;
    this.active = on;
    if (!on) this.live = null; // so no out-of-date positions show when it starts again
    this.post({ type: 'active', on });
  }

  /** Give the worker a catalogue in place of the one it has. */
  private postLoad(rows: Record<string, string>[], stdmag: Record<string, number>) {
    this.post({ type: 'load', rows, stdmag, replace: true, rev: ++this.sentRev });
  }

  /** Today's catalogue ('bright' or 'all'): the built-in snapshot at once, then the latest orbits. */
  loadData(mode: 'bright' | 'all'): Promise<void> {
    // The same catalogue already on its way, and not overtaken since: wait for that one.
    const cur = this.loading;
    if (cur && cur.mode === mode && cur.gen === this.gen) return cur.done;
    const gen = ++this.gen;
    this.requestedMode = mode;
    const done = this.fetchCatalogue(mode, gen).finally(() => {
      if (this.loading?.done === done) this.loading = null;
    });
    this.loading = { mode, gen, done };
    return done;
  }

  private async fetchCatalogue(mode: 'bright' | 'all', gen: number) {
    // Overtaken by a newer catalogue, or by a saved night being relived (which keeps its own satellites; today's
    // come back when it ends).
    const overtaken = () => gen !== this.gen || this.reliving;
    const base = import.meta.env.BASE_URL;
    let snapshot: Promise<Record<string, string>[]> | null = null;
    const snap = () => (snapshot ??= textOf(`${base}data/sat-snapshot.csv`).then(parseCSV));
    const stdmag = await fetchWithin(`${base}data/stdmag.json`, (r) => r.json() as Promise<Record<string, number>>).catch(() => ({}));
    if (overtaken()) return;
    this.stdmag = stdmag;
    this.syncClock();
    // Immediate offline snapshot so something is always there.
    let sentSnapshot = false;
    if (!this.meta.size) {
      const rows = await snap();
      if (rows.length && !overtaken()) {
        this.postLoad(rows, stdmag);
        sentSnapshot = true;
        satStatus.value = { ...satStatus.peek(), updated: null, source: 'snapshot', error: this.broken ?? undefined };
      }
    }
    const groups = mode === 'all' ? ['stations', 'visual', 'last-30-days', 'active'] : ['stations', 'visual', 'last-30-days'];
    const results = await Promise.all(groups.map((g) => loadGroup(g)));
    const rows = results.flatMap((r) => r?.rows ?? []);
    // Keep the snapshot and add the fixture rows.
    if (rows.length && useFixtures()) rows.push(...(await snap()));
    if (overtaken()) return;
    if (rows.length) {
      this.postLoad(rows, stdmag);
      const got = results.filter((r) => !!r);
      satStatus.value = {
        ...satStatus.peek(),
        updated: got.length ? Math.min(...got.map((r) => r!.fetched)) : null,
        source: got.some((r) => r!.live) ? 'live' : got.length ? 'cache' : 'snapshot',
        error: this.broken ?? undefined,
      };
    } else if (!sentSnapshot && !this.meta.size) {
      satStatus.value = { ...satStatus.peek(), updated: null, source: 'none', error: 'Satellite data unavailable offline' };
      this.firstLoaded();
    }
  }

  private onMessage(m: { type: string; [k: string]: unknown }) {
    if (m.type === 'frame') {
      if (this.active) this.live = m as unknown as Live;
    } else if (m.type === 'meta') {
      // About a catalogue already replaced: the answer about the newer one follows.
      if (m.rev !== this.sentRev) return;
      this.meta.clear();
      let newest = -Infinity;
      for (const s of m.sats as SatMeta[]) {
        this.meta.set(s.id, s);
        if (s.epoch > newest) newest = s.epoch;
      }
      newestEpoch = this.meta.size ? newest : null;
      satStatus.value = { ...satStatus.peek(), count: this.meta.size, rev: m.rev as number };
      this.firstLoaded();
    } else if (m.type === 'passes' || m.type === 'tracks' || m.type === 'trains' || m.type === 'orbit') {
      const p = this.pending.get(m.requestId as number);
      if (p) {
        this.pending.delete(m.requestId as number);
        clearTimeout(p.timer);
        p.resolve(m);
      }
    }
  }

  private failAll(why: string) {
    this.broken = why;
    for (const p of this.pending.values()) {
      clearTimeout(p.timer);
      p.reject(new Error(why));
    }
    this.pending.clear();
    // Nothing more will come from it: say so, and let anything waiting for its first catalogue go.
    satStatus.value = { ...satStatus.peek(), error: why };
    this.firstLoaded();
  }

  private request<T>(msg: DistOmit<Extract<WorkerIn, { type: 'passes' | 'tracks' | 'trains' | 'orbit' }>, 'requestId'>): Promise<T> {
    if (this.broken) return Promise.reject(new Error(this.broken));
    const requestId = ++this.reqId;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(requestId);
        reject(new Error('The satellite worker did not answer'));
      }, REQUEST_MS);
      this.pending.set(requestId, { resolve: resolve as (v: unknown) => void, reject, timer });
      // Asked before the first catalogue is in: wait for it, as an empty worker would answer "nothing".
      void this.firstLoad.then(() => this.pending.has(requestId) && this.post({ ...msg, requestId } as WorkerIn));
    });
  }

  /** Passes over the current place, or over `at` (for alerts at home while another place is shown). */
  passes(ids: number[] | 'notable', from: Date, hours: number, maxMag = 4.5, at?: { lat: number; lon: number; height: number }): Promise<Pass[]> {
    return this.request<{ passes: Pass[] }>({ type: 'passes', ids, from: from.getTime(), hours, maxMag, at }).then((r) => r.passes);
  }

  /** Visible passes of Starlink trains (fresh launches still in a line) over the coming hours. */
  trains(from: Date, hours: number): Promise<TrainPasses[]> {
    return this.request<{ trains: TrainPasses[] }>({ type: 'trains', from: from.getTime(), hours }).then((r) => r.trains);
  }

  /** The orbit data a satellite was loaded from, to keep with a sighting. */
  orbit(id: number): Promise<Record<string, string> | null> {
    return this.request<{ row: Record<string, string> | null }>({ type: 'orbit', id }).then((r) => r.row);
  }

  /**
   * Relive a saved night: show just the satellites seen then, from the orbit data saved with them (`at` is
   * when). Null puts today's catalogue back.
   */
  relive(rows: Record<string, string>[] | null, at = Date.now()) {
    if (rows) {
      orbitsFor = at;
      this.reliving = true;
      this.gen++; // today's catalogue, if still on its way, must not replace these
      this.postLoad(rows, this.stdmag);
    } else if (this.reliving) {
      orbitsFor = null;
      this.reliving = false;
      this.meta.clear();
      // Today's catalogue again, as the Satellites layer is set now (it may have changed meanwhile).
      void this.loadData(settings.peek().satellites === 'all' ? 'all' : 'bright');
    }
    this.syncActive();
  }

  tracks(from: Date, to: Date, stepS: number, maxMag: number, ids?: number[]) {
    return this.request<{ times: number[]; result: { id: number; name: string; kind: string; pts: number[] }[] }>({
      type: 'tracks', from: from.getTime(), to: to.getTime(), step: stepS * 1000, maxMag, ids,
    });
  }

  // ---------- SkyProvider ----------
  update(ctx: FrameContext) {
    const set = settings.peek();
    this.shown.length = 0;
    this.nPts = 0;
    this.trailSeg.length = 0;
    this.trailCol.length = 0;
    const L = this.live;
    if (!L || (set.satellites === 'off' && !this.reliving)) return;
    // Orbit data only predicts well for a couple of weeks either side of today.
    if (!satTimeOk(ctx.date)) return;
    const f = clamp((Date.now() - L.realT) / L.realDt, 0, 1.5);
    const sel = selection.peek();
    const selId = sel?.kind === 'satellite' ? sel.id : -1;
    const dark = L.sunAlt < -3;
    // Naked-eye limit for moving points is a little brighter than for stars. In Naked eye it is the eye's own where the
    // satellite is (against the sky's glow there), and a satellite must also be 10° up; none of the faint swarm shows (a
    // relived night's were all seen, so they show).
    const cap = set.satellites === 'all' ? 7.5 : 5.2;
    const limit = Math.min(ctx.magLimit - MOVING_MARGIN, cap);
    const eye = ctx.nakedEye && !this.reliving;
    const n = L.ids.length;
    if (this.pts.length < n * 8) this.pts = new Float32Array(n * 8 + 64);
    const cam = ctx.camera;
    const tmp = { x: 0, y: 0 };
    const now = ctx.date.getTime();
    const seenNow = new Set<number>();
    for (let i = 0; i < n; i++) {
      const id = L.ids[i];
      const o = i * STRIDE;
      const d = L.data;
      const meta = this.meta.get(id);
      if (!meta) continue;
      const isSel = id === selId;
      if (meta.kind === 'starlink' && !set.starlink && !isSel && !this.reliving) continue;
      if (!isSel && isDockedModule(meta)) continue; // drawn as part of its station
      const enu = norm([d[o] + (d[o + 3] - d[o]) * f, d[o + 1] + (d[o + 4] - d[o + 1]) * f, d[o + 2] + (d[o + 5] - d[o + 2]) * f]);
      if (enu[2] < -0.02 && !isSel) continue;
      const mag = d[o + 6] + (d[o + 7] - d[o + 6]) * f;
      const lit = d[o + 8];
      // Extinction near the horizon, as for stars.
      const s = Math.max(enu[2], 0);
      const X = 1 / (s + 0.025 * Math.exp(-11 * s));
      const magObs = mag + 0.25 * (X - 1);
      // In Naked eye, the limit where it is (eyeLimitAt) is worked out only for one bright enough to show somewhere.
      const visible = lit > 0.5 && dark && (eye
        ? enu[2] >= SAT_MIN_SIN && magObs <= Math.min(ctx.eyeSky.max - MOVING_MARGIN, cap) && magObs <= eyeLimitAt(ctx.eyeSky, enu[0], enu[1], enu[2]) - MOVING_MARGIN
        : magObs <= limit);
      const faint = !eye && set.satellites === 'all' && enu[2] > 0; // show the rest of the swarm dimly
      if (!visible && !faint && !isSel) continue;
      const p = cam.project(enu, tmp);
      const onScreen = !!p && cam.onScreen(p, 30);
      this.shown.push({ id, enu, mag: magObs, lit, x: p?.x ?? -1e4, y: p?.y ?? -1e4, onScreen });
      const dpr = ctx.dpr;
      let size: number, a: number, r = 0.72, g = 0.93, b = 1.0;
      if (visible) {
        const rel = limit + 0.5 - magObs;
        size = clamp(10 + rel * 2.4, 10, 26) * dpr;
        a = clamp(0.45 + rel * 0.28, 0.45, 1.7);
        if (meta.kind === 'station') { r = 1.0; g = 0.96; b = 0.86; }
      } else if (lit > 0.5) {
        size = 2.4 * dpr; a = 0.28;
      } else {
        size = 2.2 * dpr; a = 0.16; r = 0.9; g = 0.4; b = 0.35; // in Earth's shadow
      }
      if (isSel) { size *= 1.3; a = Math.max(a, 0.9); }
      const k = this.nPts++ * 8;
      this.pts.set([enu[0], enu[1], enu[2], size, r, g, b, a], k);
      // Short trail behind bright, visible satellites.
      if (visible) this.addHistory(id, now, enu, magObs);
      // A banner only from fresh orbits: older ones can be minutes out, and it would call something visible that is not there.
      if (visible && (meta.kind === 'station' || magObs < 0.5) && !this.visibleSet.has(id) && Math.abs(now - meta.epoch) < FRESH_ORBIT_AGE) {
        const last = this.alerted.get(id) ?? -Infinity;
        if (Math.abs(now - last) > 20 * 60e3) {
          this.alerted.set(id, now);
          this.onVisible?.(id, magObs, enu);
        }
      }
      if (visible) seenNow.add(id);
    }
    this.visibleSet = seenNow;
    this.buildTrails(now, selId);
  }

  private addHistory(id: number, t: number, v: Vec3, mag: number) {
    let h = this.history.get(id);
    if (!h) this.history.set(id, (h = []));
    const last = h[h.length - 1];
    if (last && t < last.t) h.length = 0; // time went backwards
    if (last && t - last.t < 1000 * Math.max(1, Math.abs(timeRate.peek()))) {
      last.mag = mag;
      return;
    }
    h.push({ t, v, mag });
    const span = 30e3 * Math.max(1, Math.abs(timeRate.peek()));
    while (h.length && t - h[0].t > span) h.shift();
  }

  private buildTrails(now: number, selId: number) {
    for (const [id, h] of this.history) {
      if (!h.length || Math.abs(now - h[h.length - 1].t) > 5e3 * Math.max(1, Math.abs(timeRate.peek()))) {
        this.history.delete(id);
        continue;
      }
      const span = 30e3 * Math.max(1, Math.abs(timeRate.peek()));
      const bright = clamp((5.5 - h[h.length - 1].mag) / 4, 0.25, 1);
      for (let k = 0; k + 1 < h.length; k++) {
        const a = h[k].v, b = h[k + 1].v;
        const age = clamp((now - h[k + 1].t) / span, 0, 1);
        const alpha = 0.5 * bright * (1 - age);
        this.trailSeg.push(a[0], a[1], a[2], b[0], b[1], b[2]);
        this.trailCol.push(0.5, 0.88, 1.0, alpha);
      }
    }
    // Full predicted path for the selected satellite.
    if (selId >= 0) {
      const tr = this.selTrack;
      if (!tr || tr.id !== selId || Math.abs(now - tr.at) > 30e3) {
        if (!tr || tr.id !== selId || !this.trackPending) {
          this.trackPending = true;
          const from = new Date(now - 4 * 60e3), to = new Date(now + 12 * 60e3);
          void this.tracks(from, to, 10, 99, [selId]).then((r) => {
            this.trackPending = false;
            const hit = r.result[0];
            if (hit) this.selTrack = { id: selId, times: r.times, pts: hit.pts, at: now };
          });
        }
      }
      if (tr && tr.id === selId) {
        for (let k = 0; k + 1 < tr.times.length; k++) {
          const a = tr.pts.slice(k * 5, k * 5 + 5), b = tr.pts.slice(k * 5 + 5, k * 5 + 10);
          if (a[2] < -0.05 && b[2] < -0.05) continue;
          const future = tr.times[k] > now;
          const lit = a[4] > 0.5;
          const alpha = future ? (lit ? 0.55 : 0.22) : 0.25;
          this.trailSeg.push(a[0], a[1], a[2], b[0], b[1], b[2]);
          if (lit) this.trailCol.push(1.0, 0.84, 0.55, alpha);
          else this.trailCol.push(0.8, 0.45, 0.4, alpha);
        }
      }
    }
  }
  private trackPending = false;

  points() {
    return this.nPts ? { data: this.pts, n: this.nPts } : null;
  }

  trails() {
    if (!this.trailSeg.length) return null;
    return { segments: new Float32Array(this.trailSeg), colors: new Float32Array(this.trailCol) };
  }

  labels(ctx: FrameContext, out: LabelCandidate[]) {
    const sel = selection.peek();
    const fov = ctx.camera.fovDeg;
    let starlinkLabels = 0;
    for (const s of this.shown) {
      if (!s.onScreen) continue;
      const meta = this.meta.get(s.id);
      if (!meta) continue;
      const isSel = sel?.kind === 'satellite' && sel.id === s.id;
      const station = meta.kind === 'station';
      if (!isSel && !station) {
        // Naked eye names every one it shows, as each can be seen (a Starlink train's first few).
        if (meta.kind === 'starlink' && ((fov > 40 && !ctx.nakedEye) || starlinkLabels++ > 6)) continue;
        if (s.mag > 3.5 && fov > 30 && !ctx.nakedEye) continue;
      }
      out.push({
        x: s.x, y: s.y, r: 5, text: displayName(meta), sub: s.lit > 0.5 ? `mag ${s.mag.toFixed(1)}` : 'in Earth’s shadow',
        style: station ? 'iss' : 'satellite', priority: station ? 2600 : isSel ? 2500 : 1500 - s.mag * 40,
        sel: { kind: 'satellite', id: s.id },
      });
    }
  }

  pick(_ctx: FrameContext, x: number, y: number): PickHit | null {
    let best: PickHit | null = null;
    for (const s of this.shown) {
      if (!s.onScreen) continue;
      const d = Math.hypot(s.x - x, s.y - y);
      if (d < 28 && (!best || d < best.dist)) best = { sel: { kind: 'satellite', id: s.id }, dist: d };
    }
    return best;
  }

  locate(sel: Selection): { enu: Vec3; label: string } | null {
    if (sel.kind !== 'satellite') return null;
    const s = this.shown.find((x) => x.id === sel.id);
    const meta = this.meta.get(sel.id);
    if (!meta) return null;
    if (s) return { enu: s.enu, label: displayName(meta) };
    // Not currently computed (e.g. below the horizon): use the raw frame if present.
    const L = this.live;
    if (L) {
      const i = L.ids.indexOf(sel.id);
      if (i >= 0) {
        const o = i * STRIDE;
        return { enu: norm([L.data[o], L.data[o + 1], L.data[o + 2]]), label: displayName(meta) };
      }
    }
    return null;
  }

  /** Latest raw state for the info panel. */
  stateOf(id: number): { enu: Vec3; mag: number; lit: number; range: number; altKm: number } | null {
    const L = this.live;
    if (!L) return null;
    const i = L.ids.indexOf(id);
    if (i < 0) return null;
    const o = i * STRIDE, d = L.data;
    return { enu: norm([d[o], d[o + 1], d[o + 2]]), mag: d[o + 6], lit: d[o + 8], range: d[o + 9], altKm: d[o + 10] };
  }

  /** Satellites currently visible to the naked eye (for lists). */
  visibleNow(): { id: number; mag: number; enu: Vec3 }[] {
    return this.shown.filter((s) => s.lit > 0.5 && s.enu[2] > 0).map((s) => ({ id: s.id, mag: s.mag, enu: s.enu }));
  }
}
